/**
 * Move one agreement to another status.
 *
 * WHY THIS EXISTS. There was no usable way to change a status. Jeff, after the
 * 2026-09-09 session: "there's no logical way in the UI that I've found that
 * works to update agreement statuses." He was right, in three compounding ways:
 *
 *  1. The only control lived on a DETAIL PAGE reached by clicking the hotel
 *     name in the list. Nothing said that link led to status changes, and the
 *     status pill in the row — the thing that looks like the status field — was
 *     inert.
 *  2. It was one BUTTON PER EDGE off a near-linear graph, so a Draft could only
 *     reach `rfp_sent`. There was no button that reached Accepted at all; you
 *     walked the chain, three moves and three confirm dialogs deep. "It would
 *     suck to have to go like back, back, back, back, back."
 *  3. Every move — even a routine one — opened a confirm dialog with a reason
 *     box. Stacie only ever wanted to be asked on the two that need an
 *     explanation.
 *
 * SO IT IS THE CHIP ITSELF. Jeff, on seeing the first cut as a dropdown at the
 * top of the drawer: "Rather than adding the dropdown at the top of the drawer,
 * can we make it so that you can just click the chip that's in the… column of
 * the header row?… make it clickable, then give the options in a modal (similar
 * to how we did the 'log a contact' flow)."
 *
 * Which is the right answer, and it is the one my own diagnosis pointed at
 * without my following it: the reason the control could not be found is that
 * "the status pill in the row — the thing that looks like the status field —
 * was inert." Making the thing that looks like the control BE the control beats
 * putting a second control somewhere else. It also costs the drawer nothing:
 * no group, no vertical space, and the move is available without opening a row
 * at all.
 *
 * The modal is the shared `Modal` — the same component and therefore the same
 * focus trap, Escape handling and backdrop rules as Log a contact, which is
 * what he meant by "similar to how we did the log a contact flow".
 *
 * THE OPTIONS COME FROM THE SERVER, always. `allowed_transitions` is computed
 * from `ALLOWED_TRANSITIONS` per row, for the reason the detail endpoint gives:
 * "a client-side duplicate would drift from the server's rules and start
 * offering moves the API then rejects with a 409." This component knows nothing
 * about the graph — not even that sourcing is now all-to-all. It renders what it
 * is given, in the order it is given.
 *
 * A REASON IS ASKED FOR ON CLOSURES ONLY. Stacie, 2026-09-09: "I would only
 * care if it's going to rebid requested so I can tell the hotel why we're
 * requesting a rebid, or if anyone changes it to dismissed… The other ones I
 * don't think need notes." And the reason that is a requirement rather than a
 * preference: "that's what they hated about BTP, there was a notification for
 * everything. You get 75 emails." A prompt on a routine move is the same tax in
 * a different currency.
 *
 * IT NAMES THE AUTO-ADVANCE OUT LOUD. Choosing Accepted stores `loading`
 * (`statusToStore`), because nothing rests at accepted. Silently landing
 * somewhere the user did not pick is how a form loses trust, so the result line
 * says both: "Accepted → now in Loading."
 */
import { useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  composeReason,
  promptsForReason,
  reasonsFor,
} from '@/features/hotel-supplier/status-reasons';
import { useMe } from '@/features/crm/auth/use-capabilities';

import { Modal } from '@/features/crm/components/Modal';

import {
  closesTheAgreement,
  statusBadge,
  statusLabel,
} from '../agreement-status';

export interface StatusControlProps {
  agreementId: string;
  /** The stored status now. */
  status: string;
  /**
   * The moves the SERVER permits from `status`. Empty means there are none —
   * a closed agreement — and the control says so rather than rendering a
   * dropdown with nothing in it.
   */
  allowedTransitions: readonly string[];
  /** Called after a successful write so the host can refetch. */
  onChanged?: () => void;
  /**
   * What the chip DISPLAYS, when that is not the status itself.
   *
   * The Loading tab has no Status column and must not gain one — Stacie asked
   * for it dropped there: "those are kind of duh", and "every row on this tab
   * is `loading` so the status says nothing." Its informative chip is the LOAD
   * STATE (the rate-test result), and that is the chip Jeff pointed at. So the
   * chip can show the load state while the modal it opens still moves the
   * AGREEMENT's status — the modal states the current status in words, so what
   * is being changed is never in doubt.
   */
  chipLabel?: React.ReactNode;
  /** Badge classes for the chip, when the label is not the status. */
  chipClassName?: string;
  testId?: string;
}

export function StatusControl({
  agreementId,
  status,
  allowedTransitions,
  onChanged,
  chipLabel,
  chipClassName,
  testId = 'agreement-status-control',
}: StatusControlProps) {
  const { can } = useMe();
  const canWrite = can('write:supplier');

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  /** The picked suggestion, separate from the free text so both can be kept. */
  const [reasonOption, setReasonOption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const closing = target ? closesTheAgreement(target) : false;
  /*
   * WHICH MOVES ASK WHY. The closures keep their prompt — they are
   * irreversible — and `rebid_requested` is added, because Stacie named it as
   * the other case that needs one: "so I can tell the hotel why we're
   * requesting a rebid." Everything else stays one click and silent.
   */
  const asksWhy = target ? promptsForReason(target, closesTheAgreement) : false;
  const suggestions = target ? reasonsFor(target) : null;

  /*
   * A READ-ONLY USER IS TOLD WHY, rather than shown nothing. The same choice
   * LogContactForm makes: a missing control reads as a bug or an undiscovered
   * feature, a sentence reads as a permission.
   */
  /*
   * NO CONTROL → A PLAIN CHIP, not a sentence.
   *
   * These branches used to render an explanatory paragraph, which read well in
   * a drawer and is wrong in a table cell: a column cannot carry "Your access
   * is read-only, so the status cannot be changed here." So the chip renders
   * exactly as it did before this component existed, and the explanation moves
   * to its tooltip — the column looks untouched wherever there is nothing to
   * do, which is also the honest signal.
   */
  if (!canWrite) {
    return (
      <span
        className={chipClassName ?? statusBadge(status)}
        data-testid={`${testId}-readonly`}
        title="Your access is read-only, so the status cannot be changed."
      >
        {chipLabel ?? statusLabel(status)}
      </span>
    );
  }

  if (allowedTransitions.length === 0) {
    return (
      <span
        className={chipClassName ?? statusBadge(status)}
        data-testid={`${testId}-none`}
        title="This agreement is closed — there is no move left to make. Reopening creates a new agreement, so the record of why this one closed survives."
      >
        {chipLabel ?? statusLabel(status)}
      </span>
    );
  }

  async function submit() {
    if (!target || busy) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await apiFetch<{ agreement: { status: string } }>(
        `/api/btp/agreements/${encodeURIComponent(agreementId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status: target,
            /*
             * The stored reason keeps the LABEL, not the option key — the audit
             * trail is read by people, and `other` on its own says nothing.
             * Still omitted entirely when empty: a recorded blank and "no
             * reason given" must stay distinguishable.
             */
            ...(composeReason(reasonOption, reason)
              ? { reason: composeReason(reasonOption, reason)! }
              : {}),
          }),
        },
      );
      const landed = res.agreement?.status ?? target;
      /*
       * Say where it ACTUALLY landed. `accepted` is stored as `loading`, so
       * "moved to Accepted" would be a lie the user could see through the next
       * time they looked at the row.
       */
      setDone(
        landed === target
          ? `Moved to ${statusLabel(landed)}.`
          : `${statusLabel(target)} → now in ${statusLabel(landed)}.`,
      );
      setTarget('');
      setReason('');
      setOpen(false);
      onChanged?.();
    } catch (e) {
      /*
       * The API's OWN message, not a generic one. A 409 here says what the
       * conflict was ("Cannot move an agreement from 'x' to 'y'. Allowed
       * from 'x': …"), which is the difference between a user who can act and
       * one who files a bug.
       */
      setError(
        e instanceof ApiError
          ? ((e.body as { error?: string })?.error ?? e.message)
          : 'Could not change the status. Nothing was saved.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {/*
       * THE CHIP IS THE BUTTON. Same badge classes as the read-only chip, so
       * the row looks unchanged until you hover it — the affordance is added
       * without redesigning the column.
       */}
      <button
        aria-haspopup="dialog"
        className={chipClassName ?? statusBadge(status)}
        data-testid={`${testId}-chip`}
        onClick={() => {
          setOpen(true);
          setTarget('');
          setReason('');
          setError(null);
          setDone(null);
        }}
        style={{
          border: '1px dashed var(--text-muted)',
          cursor: 'pointer',
          font: 'inherit',
        }}
        title="Change this agreement's status"
        type="button"
      >
        {chipLabel ?? statusLabel(status)}
      </button>

      {/* The result of the last move, so the row says what happened without
          the modal having to stay open to report it. */}
      {done && (
        <p
          className="muted"
          data-testid={`${testId}-done`}
          style={{ fontSize: 11, margin: '4px 0 0' }}
        >
          {done}
        </p>
      )}

      {open && (
        <Modal
          actions={
            <>
              <button
                className="btn btn-outline"
                data-testid={`${testId}-cancel`}
                disabled={busy}
                onClick={() => setOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={'btn ' + (closing ? 'btn-outline' : 'btn-primary')}
                data-testid={`${testId}-apply`}
                disabled={!target || busy}
                onClick={() => void submit()}
                type="button"
              >
                {busy
                  ? 'Saving…'
                  : closing
                    ? 'Close agreement'
                    : 'Update status'}
              </button>
            </>
          }
          onClose={() => setOpen(false)}
          testId={`${testId}-modal`}
          title="Move this agreement"
        >
          <p style={{ fontSize: 13, margin: '0 0 12px' }}>
            Currently <strong>{statusLabel(status)}</strong>. Choose where it
            goes.
          </p>

          {/*
           * ONE RADIO PER LEGAL MOVE, not a dropdown. The modal has the room a
           * table cell does not, and a visible list is scannable — which is the
           * point of moving out of the row. Server order, server list: this
           * knows nothing about the graph.
           */}
          <div
            data-testid={`${testId}-options`}
            style={{ display: 'grid', gap: 6 }}
          >
            {allowedTransitions.map((s) => (
              <label
                key={s}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <input
                  checked={target === s}
                  data-testid={`${testId}-option-${s}`}
                  name={`${testId}-target`}
                  onChange={() => {
                    setTarget(s);
                    setError(null);
                  }}
                  type="radio"
                  value={s}
                />
                <span className={statusBadge(s)}>{statusLabel(s)}</span>
                {closesTheAgreement(s) && (
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    closes it
                  </span>
                )}
              </label>
            ))}
          </div>

          {/* THE ONLY PROMPT — closures and rebids ask why, routine moves do not. */}
          {asksWhy && (
            <div
              data-testid={`${testId}-reason-wrap`}
              style={{ marginTop: 14 }}
            >
              {/*
               * A SUGGESTED list, not a closed one. The vocabulary is Stacie's
               * and only "not worth pursuing" has been confirmed so far, so the
               * picker offers her own words from the feedback log and the free
               * text below stays open — a provisional picklist must never be
               * able to refuse a real reason.
               */}
              {suggestions && (
                <>
                  <label
                    className="field-label"
                    htmlFor={`${testId}-reason-option`}
                  >
                    Reason
                  </label>
                  <select
                    className="select"
                    data-testid={`${testId}-reason-option`}
                    id={`${testId}-reason-option`}
                    onChange={(e) => setReasonOption(e.target.value)}
                    style={{ marginBottom: 10 }}
                    value={reasonOption}
                  >
                    <option value="">— choose one —</option>
                    {suggestions.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <label className="field-label" htmlFor={`${testId}-reason`}>
                {suggestions ? 'Anything to add' : 'Why (recommended)'}
              </label>
              <textarea
                className="textarea"
                data-testid={`${testId}-reason`}
                id={`${testId}-reason`}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Rate was not competitive, hotel withdrew, customer dropped the property…"
                rows={2}
                value={reason}
              />
              <p
                className="muted"
                style={{ fontSize: 11.5, margin: '4px 0 0' }}
              >
                {closing
                  ? 'This closes the agreement and cannot be undone — reopening creates a new one, so the reason survives. Recorded against your name.'
                  : 'This reason is what the hotel is told about the rebid. Recorded against your name.'}
              </p>
            </div>
          )}

          {error && (
            <div
              className="banner-error"
              data-testid={`${testId}-error`}
              style={{ marginTop: 12 }}
            >
              {error}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
